/**
 * Match a live SMS event to a port.
 * Prefer port id. Same port numbers exist on more than one gateway, so a
 * port-number-only match attaches the message to the wrong row.
 */
export function findPortForIncomingSms(ports, messageData) {
  const list = ports || [];
  const info = messageData?.port_info || null;
  const directId = Number(messageData?.port_id ?? info?.port_id ?? info?.id);
  if (Number.isFinite(directId)) {
    const byId = list.find((p) => Number(p.id) === directId);
    if (byId) return byId;
  }
  if (!info) return null;

  const num = String(info.port_number ?? "").trim();
  const gw = info.gateway_id;
  if (num && gw != null && gw !== "") {
    const byGateway = list.find(
      (p) =>
        Number(p.gateway_id) === Number(gw) &&
        String(p.port_number ?? "").trim() === num
    );
    if (byGateway) return byGateway;
  }
  if (num) {
    const matches = list.filter((p) => String(p.port_number ?? "").trim() === num);
    if (matches.length === 1) return matches[0];
  }

  const phone = String(info.assigned_number || "").replace(/\D/g, "");
  if (phone) {
    const byPhone = list.filter(
      (p) => String(p.assigned_number || "").replace(/\D/g, "") === phone
    );
    if (byPhone.length === 1) return byPhone[0];
  }
  return null;
}

/** Shape a live SMS event for the message column, or null if it is not one of these ports. */
export function liveSmsUpdate(ports, messageData) {
  const matchingPort = findPortForIncomingSms(ports, messageData);
  if (!matchingPort?.id || messageData?.id == null) return null;
  return {
    portId: matchingPort.id,
    message: {
      id: messageData.id,
      sender: messageData.sender,
      receiver: messageData.receiver,
      message: messageData.message,
      timestamp: messageData.timestamp,
      is_outgoing: messageData.is_outgoing || false,
      is_delivery_report: messageData.is_delivery_report || false,
    },
  };
}

const MESSAGE_KEEP = 10;

export function mergeMessageLists(existing, incoming, limit = MESSAGE_KEEP) {
  const byId = new Map();
  for (const msg of existing || []) {
    if (msg?.id != null) byId.set(msg.id, msg);
  }
  for (const msg of incoming || []) {
    if (msg?.id != null) byId.set(msg.id, msg);
  }
  const merged = Array.from(byId.values())
    .sort((a, b) => Number(b.id) - Number(a.id))
    .slice(0, limit);
  if (
    merged.length === (existing || []).length &&
    merged.every((msg, i) => msg.id === existing[i]?.id && msg.message === existing[i]?.message)
  ) {
    return existing || [];
  }
  return merged;
}

/** Merge a port-id → messages map without dropping a newer live message. */
export function mergePortMessageMaps(prev, incoming, limit = MESSAGE_KEEP) {
  const next = { ...(prev || {}) };
  let changed = false;
  for (const [key, msgs] of Object.entries(incoming || {})) {
    const id = Number(key);
    const prior = next[id] || next[key] || [];
    const merged = mergeMessageLists(prior, msgs, limit);
    if (merged !== prior) {
      next[id] = merged;
      if (String(id) !== String(key)) delete next[key];
      changed = true;
    }
  }
  return changed ? next : prev || {};
}
